import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { directoryApi, type SupportAccount } from '../client';

const queryKey = ['directory', 'support', 'accounts'] as const;

/** Stable query key for cache lookups and invalidations. */
useSupportAccounts.queryKey = queryKey;

/**
 * TanStack Query hook over `GET /directory/api/v1/support/accounts` — every
 * account on the platform, for the Stratum support picker.
 *
 * <p>
 * `retry: false`: the endpoint answers 404 to anybody without the `super_user`
 * claim, which is the normal case for almost every signed-in user. Retrying a
 * deliberate "you are not support" would just be three requests instead of
 * one. Callers should read a failure as "no support access", not as an error
 * worth showing.
 * </p>
 *
 * <p>
 * `staleTime: Infinity` while the tab lives: the account list changes only
 * when a customer is onboarded, and a support user re-picking an account
 * mid-session should not trigger a refetch of every account on the platform.
 * </p>
 */
export function useSupportAccounts(
  options?: Omit<UseQueryOptions<SupportAccount[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<SupportAccount[], Error>({
    queryKey,
    queryFn: directoryApi.getSupportAccounts,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    ...options,
  });
}
